import {
  Component, signal, effect, computed, ViewChild, ElementRef,
  OnDestroy, AfterViewInit, EffectRef, OnInit, untracked,
  EnvironmentInjector, runInInjectionContext
} from '@angular/core';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { PortfolioItem } from '../PortfolioItem';
import { PortfolioCategory } from '../PortfolioCategory';

@Component({
  selector: 'app-college-portfolio',
  standalone: true,
  imports: [NgFor, NgIf, NgClass, RouterLink],
  templateUrl: './college-portfolio.component.html',
  styleUrls: ['./college-portfolio.component.css']
})
export class CollegePortfolioComponent implements AfterViewInit, OnDestroy, OnInit {
  private static readonly LIGHT_FAVICON = 'favicon-light.ico';
  private static readonly DARK_FAVICON = 'favicon-dark.ico';

  year = new Date().getFullYear();
  menuOpen = signal(false);
  isLoading = signal(true);
  loadError = signal<string | null>(null);

  theme = signal<'light' | 'dark'>(
    (() => {
      try {
        const saved = localStorage.getItem('theme') as 'light' | 'dark' | null;
        if (saved === 'light' || saved === 'dark') return saved;
      } catch {}
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    })()
  );

  items = signal<PortfolioItem[]>([]);

  selectedCategory = signal<'All' | PortfolioCategory>('All');
  categories = computed(() => {
    const cats = Array.from(new Set<PortfolioCategory>(this.items().map(i => i.category)));
    return [...cats, 'All'] as Array<'All' | PortfolioCategory>;
  });
  displayedItems = computed(() =>
    this.selectedCategory() === 'All'
      ? this.items()
      : this.items().filter(i => i.category === this.selectedCategory())
  );

  pageSize = signal(9);
  visibleCount = signal(this.pageSize());
  pagedItems = computed(() => this.displayedItems().slice(0, this.visibleCount()));
  hasMore = computed(() => this.displayedItems().length > this.visibleCount());

  @ViewChild('sentinel') sentinel?: ElementRef<HTMLDivElement>;
  private io?: IntersectionObserver;
  private resetEffectRef?: EffectRef;
  private observeEffectRef?: EffectRef;
  private lastObservedEl: Element | null = null;
  private isObserving = false;
  private lastAutoLoad = 0;
  private autoLoadCooldownMs = 400;

  lightboxOpen = signal(false);
  lightboxSrc = signal<string>('');
  lightboxLoading = signal(false);
  lightboxIndex = signal<number | null>(null);
  transitionDirection = signal<'next' | 'prev' | null>(null);
  animating = signal(false);
  private static readonly TRANSITION_MS = 450; // keep in sync with the CSS animation duration
  private lightboxRequestId = 0;
  private transitionTimer: ReturnType<typeof setTimeout> | null = null;
  private swipeStartX: number | null = null;
  private swipeStartY: number | null = null;
  private swipeThreshold = 40;
  private maxVerticalDrift = 60;

  currentLightboxItem = computed(() => {
    const idx = this.lightboxIndex();
    const list = this.displayedItems();
    if (idx == null || idx < 0 || idx >= list.length) return null;
    return list[idx];
  });

  constructor(private http: HttpClient, private envInjector: EnvironmentInjector) {
    this.loadItems();

    this.resetEffectRef = effect(() => {
      const _cat = this.selectedCategory();
      const _items = this.items();
      const desired = this.pageSize();
      const current = untracked(() => this.visibleCount());
      if (current !== desired) {
        untracked(() => this.visibleCount.set(desired));
      }
    });

    effect(() => {
      const t = this.theme();
      document.documentElement.setAttribute('data-theme', t);
      this.updateFavicon(t);
      try { localStorage.setItem('theme', t); } catch {}
    });
  }

  private updateFavicon(theme: 'light' | 'dark') {
    const head = document.head || document.getElementsByTagName('head')[0];
    const favicon = document.getElementById('app-favicon');
    const next = document.createElement('link');
    next.id = 'app-favicon';
    next.rel = 'icon';
    next.type = 'image/x-icon';
    next.href = `${theme === 'dark' ? CollegePortfolioComponent.DARK_FAVICON : CollegePortfolioComponent.LIGHT_FAVICON}?v=${theme}`;

    if (favicon?.parentNode) {
      favicon.parentNode.removeChild(favicon);
    }

    head.appendChild(next);
  }

  ngAfterViewInit(): void {
    this.io = new IntersectionObserver((entries) => {
      if (!this.hasMore()) return;
      if (entries.some(e => e.isIntersecting)) {
        const now = performance.now();
        if (now - this.lastAutoLoad > this.autoLoadCooldownMs) {
          this.lastAutoLoad = now;
          this.loadMore();
        }
      }
    }, { rootMargin: '200px' });

    runInInjectionContext(this.envInjector, () => {
      this.observeEffectRef = effect(() => {
        const el = this.sentinel?.nativeElement || null;
        const canObserve = !!el && !this.isLoading() && !this.loadError() && this.hasMore();
        if (el === this.lastObservedEl && canObserve === this.isObserving) return;
        untracked(() => {
          if (this.isObserving) {
            try { this.io?.unobserve(this.lastObservedEl as Element); } catch {}
            this.isObserving = false;
          }
          this.lastObservedEl = el;
          if (canObserve && el) {
            this.io?.observe(el);
            this.isObserving = true;
          }
        });
      });
    });
  }

  ngOnDestroy(): void {
    if (this.isObserving && this.lastObservedEl) {
      try { this.io?.unobserve(this.lastObservedEl); } catch {}
    }
    this.io?.disconnect();
    this.observeEffectRef?.destroy();
    this.resetEffectRef?.destroy();
    if (this.transitionTimer) clearTimeout(this.transitionTimer);
    this.setLightboxScrollLock(false);
    window.removeEventListener('keydown', this.keyHandler);
  }

  ngOnInit(): void {
    window.addEventListener('keydown', this.keyHandler);
  }

  private loadItems() {
    this.isLoading.set(true);
    this.loadError.set(null);
    this.http.get<any[]>('assets/portfolio-college-items.json').subscribe({
      next: (raw) => {
        const validCategories = new Set(Object.values(PortfolioCategory));
        const mapped = raw.map((i) => ({
          ...i,
          category: validCategories.has(i.category)
            ? (i.category as PortfolioCategory)
            : PortfolioCategory.General
        })) as PortfolioItem[];
        mapped.forEach(it => {
          const sFull = this.sanitizeImagePath(it.full);
          if (sFull) it.full = sFull;
          const sSrc = this.sanitizeImagePath(it.src);
          if (sSrc) it.src = sSrc;
        });
        this.items.set(mapped);
        if (mapped.length && this.selectedCategory() === 'All') {
          this.selectedCategory.set(mapped[0].category);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load college portfolio items', err);
        this.loadError.set('Unable to load portfolio items. Please try again.');
        this.isLoading.set(false);
      }
    });
  }

  toggleMenu() { this.menuOpen.update(v => !v); }
  toggleTheme() { this.theme.update(t => (t === 'dark' ? 'light' : 'dark')); }
  selectCategory(cat: 'All' | PortfolioCategory) { this.selectedCategory.set(cat); }
  trackByIndex = (_: number, __: PortfolioItem) => _;
  trackByString = (_: number, s: string) => s;

  onImgLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    img.classList.add('loaded');
  }

  loadMore() {
    const current = this.visibleCount();
    const total = this.displayedItems().length;
    if (current >= total) return;
    this.visibleCount.set(Math.min(current + this.pageSize(), total));
  }

  openLightbox(index: number) {
    const list = this.displayedItems();
    if (index < 0 || index >= list.length) return;
    this.lightboxOpen.set(true);
    this.lightboxLoading.set(true);
    this.lightboxIndex.set(index);
    const rawPath = list[index].full || list[index].src;
    const safe = this.sanitizeImagePath(rawPath);
    if (!safe) {
      this.lightboxLoading.set(false);
      this.setLightboxScrollLock(true);
      return;
    }
    this.preloadLightboxImage(safe, index, null);
    this.setLightboxScrollLock(true);
  }

  closeLightbox() {
    this.lightboxOpen.set(false);
    this.lightboxSrc.set('');
    this.lightboxLoading.set(false);
    this.lightboxIndex.set(null);
    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }
    this.animating.set(false);
    this.transitionDirection.set(null);
    this.setLightboxScrollLock(false);
  }

  nextImage() {
    if (!this.lightboxOpen()) return;
    const list = this.displayedItems();
    const idx = this.lightboxIndex();
    if (idx == null) return;
    this.updateImageWithAnimation((idx + 1) % list.length, 'next');
  }

  prevImage() {
    if (!this.lightboxOpen()) return;
    const list = this.displayedItems();
    const idx = this.lightboxIndex();
    if (idx == null) return;
    this.updateImageWithAnimation((idx - 1 + list.length) % list.length, 'prev');
  }

  private updateImageWithAnimation(newIndex: number, direction: 'next' | 'prev') {
    const list = this.displayedItems();
    const item = list[newIndex];
    if (!item) return;
    const safe = this.sanitizeImagePath(item.full || item.src);
    if (!safe) return;
    // Note: the current image stays visible (no lightboxLoading hide) while the
    // next one preloads, so nothing flickers or blanks out during the fetch.
    this.preloadLightboxImage(safe, newIndex, direction);
  }

  private preloadLightboxImage(src: string, index: number, direction: 'next' | 'prev' | null) {
    if (!this.lightboxOpen()) return;
    const requestId = ++this.lightboxRequestId;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      if (!this.lightboxOpen() || requestId !== this.lightboxRequestId) return;
      this.lightboxIndex.set(index);
      this.lightboxSrc.set(src);
      this.lightboxLoading.set(false);
      if (direction) this.playTransition(direction);
    };
    img.onerror = () => {
      if (!this.lightboxOpen() || requestId !== this.lightboxRequestId) return;
      this.lightboxLoading.set(false);
    };
    img.src = src;
  }

  // Plays the slide/fade CSS animation on a fixed timeline of its own, starting
  // only once the new image is actually in the DOM — never while still waiting
  // on the network — so the animation and the image swap are never out of sync.
  private playTransition(direction: 'next' | 'prev') {
    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }
    const start = () => {
      this.transitionDirection.set(direction);
      this.animating.set(true);
      this.transitionTimer = setTimeout(() => {
        this.animating.set(false);
        this.transitionDirection.set(null);
        this.transitionTimer = null;
      }, CollegePortfolioComponent.TRANSITION_MS);
    };
    if (this.animating()) {
      this.animating.set(false);
      requestAnimationFrame(start);
    } else {
      start();
    }
  }

  onTouchStart(e: TouchEvent) {
    if (!this.lightboxOpen()) return;
    this.swipeStartX = e.touches[0].clientX;
    this.swipeStartY = e.touches[0].clientY;
  }
  onTouchMove(_e: TouchEvent) {}
  onTouchEnd(e: TouchEvent) {
    if (this.swipeStartX == null || this.swipeStartY == null) return;
    const dx = e.changedTouches[0].clientX - this.swipeStartX;
    const dy = e.changedTouches[0].clientY - this.swipeStartY;
    if (Math.abs(dx) > this.swipeThreshold && Math.abs(dy) < this.maxVerticalDrift) {
      if (dx < 0) this.nextImage(); else this.prevImage();
    }
    this.swipeStartX = this.swipeStartY = null;
  }

  private keyHandler = (e: KeyboardEvent) => {
    if (!this.lightboxOpen()) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); this.nextImage(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); this.prevImage(); }
    else if (e.key === 'Escape') { this.closeLightbox(); }
  };

  private setLightboxScrollLock(locked: boolean) {
    const root = document.documentElement;
    const body = document.body;
    if (locked) {
      root.classList.add('lightbox-open');
      body.classList.add('lightbox-open');
    } else {
      root.classList.remove('lightbox-open');
      body.classList.remove('lightbox-open');
    }
  }

  private sanitizeImagePath(path: any): string | null {
    if (typeof path !== 'string') return null;
    let p = path.trim();
    if (!p) return null;
    if (/^https?:\/\//i.test(p)) return p;
    p = p.replace(/^\/+/, '');
    if (!p.toLowerCase().startsWith('assets/')) p = 'assets/' + p;
    return p;
  }
}
